import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Progress } from '@/components/ui/Progress';
import {
  getUserProfileDisplayName,
  getUserProfileStatusColoursByPercentage,
  isUserProfileComplete
} from '@/lib/helpers/userProfile';
import { cn } from '@/lib/helpers/utils';
import { useMediaQuery } from '@/lib/hooks';
import { WindowLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { omit } from 'lodash';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import { useAppDispatch } from '../../app/redux/hooks';
import { AUTH_LEVEL } from '../../app/services/authTypes';
import { FieldData, User } from '../../app/user/profile/profileUtils';
import { Button, ButtonProps } from '../ui/Button';
import withLocation from '../withLocation';

interface UserProfileProps extends React.HTMLAttributes<HTMLDivElement> {
  user: User;
  authLevel: AUTH_LEVEL;
  profile: FieldData | null;
}

/**
 * Render the user name, univiersity and initial avatar.
 */
export const UserProfile = ({ user, profile, authLevel, className, ...props }: UserProfileProps) => {
  const name: string = getUserProfileDisplayName(user, authLevel);
  return (
    <div className={cn('group grid grid-cols-[max-content_1fr] gap-2 [&.compact]:gap-0', className)} {...props}>
      <Avatar>
        <AvatarFallback className="bg-primary text-primary-foreground">
          {user?.firstName?.charAt(0)}
          {user?.lastName?.charAt(0)}
        </AvatarFallback>
      </Avatar>
      <div className="justify-center flex flex-col space-y-1 group-[.compact]:hidden">
        {name && <p className="font-semibold text-gray text-lg leading-5 line-clamp-2">{name}</p>}
        {profile?.university && <p className="line-clamp-2 text-gray-700 text-sm">{profile?.university?.value}</p>}
      </div>
    </div>
  );
};

interface UserProfileCompletionRingProps extends React.HTMLAttributes<HTMLDivElement> {
  user: User;
  compact?: boolean;
}

/**
 * Render the user profile completion ring
 */
export const UserProfileCompletionRing = ({
  user,
  compact = false,
  className,
  ...props
}: UserProfileCompletionRingProps) => {
  const profileCompletionRingStrokeWidth = compact ? 4 : 6;
  const profileCompletionRingRadius = compact ? 24 : 56;
  const profileCompletionRingPercent: number = user.profileCompletion;
  const profileCompletionRingCircum =
    2 * (profileCompletionRingRadius - profileCompletionRingStrokeWidth / 2) * Math.PI;
  const profileCompletionRingDraw = (profileCompletionRingPercent * profileCompletionRingCircum) / 100;
  const profileStatusColours = getUserProfileStatusColoursByPercentage(profileCompletionRingPercent);

  return (
    <div className={cn(`grid place-content-center`, className)} {...props}>
      <svg
        className={cn(
          'relative -rotate-90 transition-all ease-in-out duration-75',
          profileStatusColours.fill,
          profileStatusColours.stroke,
          compact ? 'size-12' : 'size-28'
        )}
        style={{ gridArea: '1/1' }}
      >
        <circle
          cx={profileCompletionRingRadius}
          cy={profileCompletionRingRadius}
          r={profileCompletionRingRadius - profileCompletionRingStrokeWidth / 2}
          style={{
            strokeDasharray: `${profileCompletionRingDraw} 999`,
            strokeWidth: profileCompletionRingStrokeWidth,
            strokeLinecap: 'round'
          }}
        />
      </svg>
      <div className={`flex flex-col justify-center items-center z-0`} style={{ gridArea: '1/1' }}>
        <p className={cn('font-bold text-gray text-3xl', compact && 'text-xs')}>{profileCompletionRingPercent}%</p>
        {!compact && <p className={`text-xxs uppercase font-bold -mt-1 text-gray`}>complete</p>}
      </div>
    </div>
  );
};

interface UserProfileCompletionProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  percentage: number;
  disabledPercentageLabel: boolean;
}

/**
 * Render the user profile completion progress bar
 */
export const UserProfileCompletionProgressBar = ({
  className,
  percentage,
  disabledPercentageLabel,
  ...props
}: UserProfileCompletionProgressBarProps) => {
  const profileStatusColours = getUserProfileStatusColoursByPercentage(percentage);
  return (
    <div className={cn('flex items-center space-x-1 w-full', className)} {...props}>
      <Progress value={percentage} className="h-2" indicatorProps={{ className: profileStatusColours.background }} />
      {!disabledPercentageLabel && <span className="font-bold text-xs">{percentage}%</span>}
    </div>
  );
};

interface UserProfileCompletionProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  location: WindowLocation<unknown>;
  user: User;
  disabledPercentageLabel: boolean;
  buttonProps?: ButtonProps;
}

/**
 * Render user profile completion progress
 */
export const UserProfileCompletionProgress = withLocation(
  ({
    location,
    className,
    user,
    disabledPercentageLabel,
    buttonProps,
    ...props
  }: UserProfileCompletionProgressProps) => {
    const dispatch = useAppDispatch();
    const isDesktop = useMediaQuery();

    const profileCompleted: boolean = isUserProfileComplete(user);
    const buttonLabel: string = profileCompleted ? 'View profile' : 'Complete profile';

    return (
      <div
        className={cn(
          'flex space-x-2 items-center w-full md:flex-col md:space-x-0 md:space-y-3 md:items-start',
          className
        )}
        {...props}
      >
        <UserProfileCompletionProgressBar
          percentage={user?.profileCompletion || 0}
          disabledPercentageLabel={disabledPercentageLabel}
        />
        <div className="flex-shrink-0">
          <Button
            size="sm"
            icon={isDesktop ? false : true}
            aria-label={buttonLabel}
            onClick={(event) => {
              buttonProps?.onClick && buttonProps?.onClick(event);
              navigate('/dashboard/profile');
            }}
            {...omit(buttonProps, ['onClick'])}
          >
            {isDesktop ? (
              <span>{profileCompleted ? 'View profile' : 'Complete profile'}</span>
            ) : (
              <ArrowRight className="w-4" />
            )}
          </Button>
        </div>
      </div>
    );
  }
);
